import { useState } from 'react'
import { Dialog } from '../../../components/ui/dialog'
import { Amenity } from '../data'
import { AmenitiesWidgetConfig } from '../config'
import { useWidget } from '../../../providers/widget'
import { useLocaleDetection } from '../../../hooks/locale'
import { useLocalizedTranslation } from '../../../common/i18n'
import ResponsiveDialogContent from '../../../components/ResponsiveDialogContent'
import { useAmenitiesCss } from '../hooks/useAmenitiesCss'
import { resolveAmenityDisplayIcon, type AmenityIconResolution } from '../iconResolver'
import AmenityIcon from './AmenityIcon'

interface AmenitiesListProps {
  amenities: Amenity[]
}

/**
 * Renders one resolved icon. Both grids render through AmenityRow below, so
 * the excerpt and the "Show More" dialog always agree on what an amenity's
 * icon is — before #1423 they didn't (the dialog fell back to a hardcoded
 * `icon-demo` class instead of the configurable `defaultIcon`).
 */
function AmenityIconCell({ resolution }: { resolution: AmenityIconResolution }) {
  if (resolution.kind === 'svg') {
    return <AmenityIcon iconId={resolution.iconId} />
  }
  if (resolution.kind === 'class') {
    return <span className={resolution.className}></span>
  }
  return null
}

/**
 * One amenity row, shared by the excerpt grid and the "Show More" dialog grid.
 * Resolves the icon once (override -> Central `icon_class` -> keyword resolver
 * -> fallback, #1423). A `none` resolution omits the icon wrapper entirely —
 * keeping it would leave a fixed-width empty gutter and gap in front of the
 * name.
 */
function AmenityRow({ amenity, options }: { amenity: Amenity; options: AmenitiesWidgetConfig }) {
  const resolution = resolveAmenityDisplayIcon(amenity, options)
  return (
    <div className="amenity-item aminity-item flex items-start gap-2">
      {resolution.kind !== 'none' && (
        <div className="amenity-icon aminity-icon">
          <AmenityIconCell resolution={resolution} />
        </div>
      )}
      <div className="amenity-name aminity-name">
        {amenity.name}
      </div>
    </div>
  )
}

export default function AmenitiesList({ amenities }: AmenitiesListProps) {
  const { options, resolvedTheme } = useWidget<AmenitiesWidgetConfig>()
  const cssString = useAmenitiesCss()
  const [dialogOpen, setDialogOpen] = useState(false)

  const visibleAmenities = amenities.slice(0, options.countInExcerpt)
  const hasMore = amenities.length > options.countInExcerpt

  const { detectedLocale } = useLocaleDetection(options.language.useAutoDetectedLocale, options.language.locale)
  const { _t } = useLocalizedTranslation('amenities', options.translations, detectedLocale, options.language)

  return (
    <div
      className="w-full"
      style={{
        '--amenities-columns-sm': options.columns.sm,
        '--amenities-columns-md': options.columns.md,
        '--amenities-columns-lg': options.columns.lg,
      } as React.CSSProperties}
    >
      <div className="amenities-grid amenities-grid-responsive aminities-grid aminities-grid-responsive">
        {visibleAmenities.map((amenity) => (
          <AmenityRow key={amenity.id} amenity={amenity} options={options} />
        ))}
      </div>

      {hasMore && options.showMoreButton && (
        <>
          <div className="show-more-button-container">
            <button
              className="bg-accent text-primary-foreground hover:bg-accent/90 px-4 py-2 rounded-md font-medium transition-colors"
              onClick={() => setDialogOpen(true)}
            >
              {_t('showMoreButton', 'Show More')}
            </button>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <ResponsiveDialogContent
              className={`homerunner-amenities-dialog homerunner-aminities-dialog max-h-[90vh] p-4 md:p-6 overflow-y-auto ${resolvedTheme === 'dark' ? 'dark' : ''}`}
              style={{ '--content-max-width': '1280px' } as React.CSSProperties}
              elevateDom
              cssString={cssString}
              title="Amenities"
            >
              <div className="overflow-y-auto max-h-[70vh] pr-2"
              style={{
                '--amenities-columns-sm': Math.max(options.columns.sm-1, 1),
                '--amenities-columns-md': Math.max(options.columns.md-1, 1),
                '--amenities-columns-lg': Math.max(options.columns.lg-1, 1),
              } as React.CSSProperties}>
                <div className="amenities-grid amenities-grid-responsive aminities-grid aminities-grid-responsive">
                  {amenities.map((amenity) => (
                    <AmenityRow key={amenity.id} amenity={amenity} options={options} />
                  ))}
                </div>
              </div>
            </ResponsiveDialogContent>
          </Dialog>
        </>
      )}
    </div>
  )
}
